/**
 * Service-worker registration, the token handshake, and the liveness probe.
 *
 * Phase 4 of `docs/plans/manuscript-file-delivery.md` (§6.2, §6.4). The worker
 * itself is `public/sw.js` — read it first; this module is only the page's half
 * of the contract.
 *
 * Nothing here ever puts the token in a URL (§6.3). The token travels by
 * `postMessage` and by nothing else.
 */

/** Root path ⇒ scope `/` ⇒ the worker controls the SPA and `/api/v1/*` alike. */
const SW_URL = '/sw.js';

/** Must match `PING_PREFIX` / `PING_HEADER` / `PING_VALUE` in `public/sw.js`. */
const PING_PREFIX = '/sw-ping/';
const PING_HEADER = 'X-SW';
const PING_VALUE = 'pong';

/** As tweb does — the probe result is cached for a few seconds, not per call. */
const PING_TTL_MS = 10_000;
/** A worker that has not answered in this long is treated as dead, not slow. */
const PING_TIMEOUT_MS = 3_000;
/** The handshake ack is a local round trip; anything slower is a dead worker. */
const HANDSHAKE_TIMEOUT_MS = 3_000;

/**
 * iOS Safari is routed to the degraded path (§6.5), inherited from tweb's
 * `USE_SW = !IS_MOBILE_SAFARI && …` rather than measured here. Recorded as an
 * open question in the plan (§12.7): ONE real device settles it, and until one
 * does, the cautious side is the one that still delivers files.
 *
 * The UA sniff is deliberate. There is no feature test for "the service worker
 * download path is broken on this browser", and every iOS browser is WebKit, so
 * the platform check is the honest one.
 */
export const isIosSafari = (): boolean => {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOS = /iP(hone|ad|od)/.test(ua) ||
    // iPadOS 13+ reports itself as a Mac; the touch points give it away.
    (/Macintosh/.test(ua) && typeof document !== 'undefined' && navigator.maxTouchPoints > 1);
  if (!iOS) return false;
  // Every browser on iOS is WebKit, so Chrome/Firefox/Edge on iOS count too.
  return true;
};

/** Whether registering is even worth attempting. */
export const swCapable = (): boolean =>
  typeof navigator !== 'undefined' &&
  'serviceWorker' in navigator &&
  // Service workers need a secure context. On the box this is always true
  // (https); on a plain-http dev host it is false and the app must still work.
  typeof window !== 'undefined' && window.isSecureContext === true &&
  !isIosSafari();

let registration: ServiceWorkerRegistration | null = null;
let registering: Promise<ServiceWorkerRegistration | null> | null = null;

/** The last token handed to `publishAuthToken`, replayed on `controllerchange`. */
let lastToken: string | null = null;

/**
 * Register once, idempotently.
 *
 * `updateViaCache: 'none'` so the browser re-validates `sw.js` itself on every
 * update check rather than serving it from the HTTP cache — the nginx
 * `location = /sw.js` block (§10) is the other half of that, and a regex
 * `location` for `.js` files beats every prefix location, which is why the
 * exact-match block is required rather than merely tidy.
 */
export const registerMediaSw = (): Promise<ServiceWorkerRegistration | null> => {
  if (!swCapable()) return Promise.resolve(null);
  if (registering) return registering;

  registering = navigator.serviceWorker
    .register(SW_URL, { scope: '/', updateViaCache: 'none' })
    .then((reg) => {
      registration = reg;
      return reg;
    })
    .catch((error) => {
      // A failed registration is not fatal: every media path has a degraded
      // form (§6.5). It must be visible, though — a silent failure here looks
      // exactly like an auth bug from the reviewer's side.
      console.warn('media service worker registration failed', error);
      return null;
    });

  // A new worker taking control invalidates both the handshake (the new worker
  // has no token) and the cached probe result.
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    pingCache = null;
    void publishAuthToken(lastToken);
  });

  return registering;
};

/**
 * Hand the bearer token to the worker, or clear it.
 *
 * Called on boot (with whatever `localStorage` already holds), on every login,
 * on every logout with `null`, and on `controllerchange`. Resolves true only
 * when the worker acknowledged — the SPA uses that to decide whether the
 * service-worker path is usable at all, so a silent failure must resolve false
 * rather than throw.
 */
export const publishAuthToken = async (token: string | null): Promise<boolean> => {
  lastToken = token;
  if (!swCapable()) return false;

  // `ready` resolves as soon as there is an ACTIVE worker for this scope, which
  // is not the same as this page being controlled by it — hence the second
  // check. On the very first load after registration the page is uncontrolled
  // and `controller` is null (§6.4 condition 2).
  const target = await Promise.race([
    navigator.serviceWorker.ready.then((reg) => reg.active),
    new Promise<null>((resolve) => window.setTimeout(() => resolve(null), HANDSHAKE_TIMEOUT_MS)),
  ]);
  if (!target) return false;

  return new Promise<boolean>((resolve) => {
    const channel = new MessageChannel();
    const timer = window.setTimeout(() => {
      channel.port1.close();
      resolve(false);
    }, HANDSHAKE_TIMEOUT_MS);

    channel.port1.onmessage = (event) => {
      window.clearTimeout(timer);
      channel.port1.close();
      resolve(!!(event.data && event.data.ok));
    };

    try {
      target.postMessage({ type: 'auth', token }, [channel.port2]);
    } catch (error) {
      window.clearTimeout(timer);
      channel.port1.close();
      console.warn('media service worker handshake failed', error);
      resolve(false);
    }
  });
};

let pingCache: { at: number; live: boolean } | null = null;

/**
 * The three conditions of §6.4, all required, in the order that makes the
 * cheapest one fail first.
 *
 * The third is a `fetch` and NOT tweb's hidden iframe: this vhost serves the
 * SPA with a `try_files … /index.html` fallback, so a request no worker answers
 * comes back **200 index.html** and an iframe fires `load` — a false pass that
 * would route every download at a URL nothing will authorise. A response header
 * cannot be forged by index.html, so the header is the test.
 *
 * An idle worker is killed silently and gives a HANGING request rather than an
 * error, which is why the probe carries its own timeout.
 */
export const isSwLive = async (): Promise<boolean> => {
  if (!swCapable()) return false;

  const now = Date.now();
  if (pingCache && now - pingCache.at < PING_TTL_MS) return pingCache.live;

  const live = await probe();
  pingCache = { at: Date.now(), live };
  return live;
};

/** Drop the cached probe result — after a 401, or when the worker changes. */
export const invalidateSwProbe = (): void => {
  pingCache = null;
};

const probe = async (): Promise<boolean> => {
  try {
    if (!registration) await registerMediaSw();
    await Promise.race([
      navigator.serviceWorker.ready,
      new Promise<void>((_, reject) =>
        window.setTimeout(() => reject(new Error('sw not ready')), PING_TIMEOUT_MS)),
    ]);
    if (!navigator.serviceWorker.controller) return false;

    const controller = new AbortController();
    const timer = window.setTimeout(() => controller.abort(), PING_TIMEOUT_MS);
    try {
      // The random suffix defeats every cache between here and the worker.
      const response = await fetch(`${PING_PREFIX}${Math.random().toString(36).slice(2)}`, {
        signal: controller.signal,
        cache: 'no-store',
      });
      return response.headers.get(PING_HEADER) === PING_VALUE;
    } finally {
      window.clearTimeout(timer);
    }
  } catch {
    return false;
  }
};
